
import React, { useState, useEffect, useMemo } from 'react';
import { BlogFeaturedImage } from '@/components/blog/BlogFeaturedImage';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import { Search, Plus, Clock, ArrowRight, Camera, Star, Target } from 'lucide-react';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { getAllPosts, getAllCategories } from '@/lib/blog';
import type { BlogPost, BlogCategory } from '@/lib/blog';
import { supabase } from '@/integrations/supabase/client';

const POSTS_PER_PAGE = 9;

function formatCategoryName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const Blog: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  const allPosts = getAllPosts();
  const categories = getAllCategories();

  // Check if user is admin
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
        if (!error) {
          setIsAdmin(data || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const filteredPosts = useMemo(() => {
    let posts = allPosts;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          p.excerpt.toLowerCase().includes(term),
      );
    }

    if (selectedCategory && selectedCategory !== 'all') {
      posts = posts.filter((p) => p.category === selectedCategory);
    }

    return posts;
  }, [allPosts, searchTerm, selectedCategory]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(POSTS_PER_PAGE);
  }, [searchTerm, selectedCategory]);

  // Get featured post (most recent)
  const featuredPost = filteredPosts[0];
  const allRemainingPosts = filteredPosts.slice(1);
  const remainingPosts = allRemainingPosts.slice(0, visibleCount);
  const hasMorePosts = allRemainingPosts.length > visibleCount;

  return (
    <>
      <SEOHead
        title="Astrophotography Blog — Milky Way Tips, Guides, and Gear"
        description="Learn how to photograph the Milky Way with expert guides, camera settings, gear reviews, dark sky location tips, and night photography techniques."
        keywords="astrophotography blog, how to photograph milky way, milky way photography tips, night sky photography guide, astrophotography settings, best camera for astrophotography, dark sky photography, star photography tips, astrophotography for beginners"
        canonicalUrl="/blog"
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Blog</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Tips, Guides & Planning
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Practical advice for astrophotographers — from planning your first Milky Way shoot to advanced techniques.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Search & filters */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              {isAdmin && (
                <Link to="/admin">
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Manage Blog
                  </Button>
                </Link>
              )}
            </div>

            {/* Category filter buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-foreground text-background'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                All ({allPosts.length})
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.slug
                      ? 'bg-foreground text-background'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>

          {filteredPosts.length > 0 ? (
            <div className="space-y-10">
              {/* Featured Post */}
              {featuredPost && (
                <div>
                  <Link to={`/blog/${featuredPost.slug}`}>
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow group border-border">
                      {featuredPost.featured_image && (
                        <BlogFeaturedImage
                          src={featuredPost.featured_image}
                          alt={featuredPost.title}
                          frameClassName="aspect-[21/9] bg-muted"
                          imageClassName="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                          width={1200}
                          quality={80}
                        />
                      )}
                      <CardContent className="p-6 md:p-8">
                        <div className="flex items-center gap-3 mb-3">
                          {featuredPost.category && (
                            <Badge variant="secondary" className="text-xs">
                              {formatCategoryName(featuredPost.category)}
                            </Badge>
                          )}
                          <span className="text-sm text-muted-foreground">
                            {formatDate(featuredPost.published_at)}
                          </span>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {featuredPost.reading_time} min
                          </span>
                        </div>

                        <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors leading-tight">
                          {featuredPost.title}
                        </h2>

                        <p className="text-muted-foreground leading-relaxed mb-4 max-w-3xl">
                          {featuredPost.excerpt}
                        </p>

                        <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                          Read article
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>

                  <NewsletterSignup variant="blog" className="mt-8" />
                </div>
              )}

              {/* Posts Grid */}
              {remainingPosts.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-5">More Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {remainingPosts.map((post) => (
                      <Link key={post.slug} to={`/blog/${post.slug}`}>
                        <Card className="h-full hover:shadow-lg transition-shadow group overflow-hidden border-border">
                          {post.featured_image && (
                            <BlogFeaturedImage
                              src={post.featured_image}
                              alt={post.title}
                              frameClassName="aspect-[4/3]"
                              imageClassName="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                              width={384}
                            />
                          )}
                          <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                              {post.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {formatCategoryName(post.category)}
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatDate(post.published_at)}
                              </span>
                            </div>

                            <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                              {post.title}
                            </h3>

                            <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 mb-3">
                              {post.excerpt}
                            </p>

                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.reading_time} min read
                            </span>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                  {hasMorePosts && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        onClick={() => setVisibleCount((prev) => prev + POSTS_PER_PAGE)}
                      >
                        Load More
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        Showing {visibleCount} of {allRemainingPosts.length}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-lg font-semibold text-foreground mb-2">No posts found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || (selectedCategory && selectedCategory !== 'all')
                  ? 'Try adjusting your search or filter.'
                  : 'Check back soon for new content.'}
              </p>
              {(searchTerm || (selectedCategory && selectedCategory !== 'all')) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}

        {/* CTA strip — Plan, Workshops, Premium */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-gold" />
              <h4 className="font-semibold text-white">Plan Your Shoot</h4>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-3">
              Free Milky Way calendar with nightly visibility ratings.
            </p>
            <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
              Open Planner <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
          <Link to="/workshops" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-5 h-5 text-gold" />
              <h4 className="font-semibold text-white">Browse Workshops</h4>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-3">
              Astrophotography workshops at dark sky locations.
            </p>
            <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
              Find a Workshop <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
          {(!user || !isSubscribed) ? (
            <Link to="/register" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-foreground">Go Premium</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Moon Planner, Trip Planner, exports — $25/year.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                Start Premium <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ) : (
            <Link to="/workshops/instructors" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Meet Instructors</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Browse instructor profiles and find your next workshop.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                View Instructors <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          )}
        </div>

        </div>
      </div>
    </>
  );
};

export default Blog;
